import React, { useEffect, useState } from "react";

import { attendancesService } from "api/services/attendances.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Table, { Column } from "components/UI/Table";
import { extractList, getApiErrorMessage } from "utils/api-error";

import type { AttendanceItem } from "api/types/attendances.types";
import "pages/AdminPage.scss";
import "./AttendanceList.scss";

const TYPE_LABEL: Record<string, string> = {
  session: "강습",
  session_with_practice: "강습+연습",
  practice: "연습",
};

const AttendanceList: React.FC = () => {
  const [items, setItems] = useState<AttendanceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await attendancesService.list(params);
      setItems(extractList<AttendanceItem>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const columns: Column<AttendanceItem>[] = [
    { key: "date", title: "날짜", width: "110px" },
    {
      key: "type",
      title: "유형",
      width: "100px",
      render: (v) => TYPE_LABEL[String(v)] ?? String(v),
    },
    {
      key: "start_time",
      title: "시간",
      width: "120px",
      render: (v, row) => `${String(v).slice(0, 5)} ~ ${String(row.end_time).slice(0, 5)}`,
    },
    { key: "location_name", title: "장소", width: "100px" },
    { key: "instructor_name", title: "강사", width: "90px" },
    { key: "sport_name", title: "종목", width: "80px" },
    {
      key: "session",
      title: "강습 정보",
      width: "180px",
      render: (v) => {
        if (!v) return "-";
        const s = v as AttendanceItem["session"];
        return s ? `${s.name} (${s.student_count}/${s.capacity})` : "-";
      },
    },
    {
      key: "practice",
      title: "연습 정보",
      width: "180px",
      render: (v) => {
        if (!v) return "-";
        const p = v as AttendanceItem["practice"];
        return p ? `${p.name} (${p.student_count}/${p.capacity})` : "-";
      },
    },
    { key: "total_student_count", title: "총 인원", width: "70px" },
    {
      key: "is_attendance_completed",
      title: "출석 완료",
      width: "80px",
      render: (v) => (v ? <Badge variant="active">완료</Badge> : <Badge variant="pending">미완료</Badge>),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="출석 관리" description="출석 기록을 조회합니다." />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <span className="attendance-filter-label">기간</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="attendance-date-input" />
          <span className="attendance-separator">~</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="attendance-date-input" />
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={items} rowKey="id" />}
      </div>
    </div>
  );
};

export default AttendanceList;
